// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Checkbox, Chip, FormControl, InputLabel, ListItemText, MenuItem, OutlinedInput, Select } from "@mui/material";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePopup from "@/hooks/usePopup";
import useStoreLanguages from "@/hooks/useStoreLanguages";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse, TyEditorCategory, TyEditorProduct } from "@/types";
import ActionsBar from "@/UI/components/ActionsBar";
import SortCategoriesPopup from "@/UI/popups/SortCategoriesPopup";
import { getTextFieldSx, hasAnyLangValue, pickLangValue } from "@/utils";
import CategoriesGrid from "./CategoriesGrid";
import ProductsGrid from "./ProductsGrid";
import {
    categoryPayload,
    emptyCategoryDraft,
    emptyProductDraft,
    isRowDirty,
    nextRowKey,
    productPayload,
    toCategoryRow,
    toProductRow,
    type TyCategoryDraft,
    type TyProductDraft,
    type TyRow,
} from "./model";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type TyTab = "categories" | "products";

type TyEditorData = { categories: TyEditorCategory[]; products: TyEditorProduct[] };

// --- Logic --------------------------------------------------------------------------------------

/**
 * A spreadsheet for the whole menu, for the freelancers who type stores in for us. It is reachable
 * only by URL — nothing links here — but it lives inside the dashboard layout, so the store switcher,
 * the account menu and the actions bar are the same ones as everywhere else.
 */
export default function MenuEditorScreen() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { languages, defaultLang } = useStoreLanguages();
    const { addComp, removeComp } = usePopup();

    const [tab, setTab] = useState<TyTab>("categories");
    const [categoryRows, setCategoryRows] = useState<TyRow<TyCategoryDraft>[]>([]);
    const [productRows, setProductRows] = useState<TyRow<TyProductDraft>[]>([]);
    /** Narrows the products sheet only — saving, dirty counts and validation always span every row. */
    const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

    /** The row to scroll to once it has rendered, and which column to land in when Enter created it. */
    const pendingScrollKey = useRef<{ key: string; column: string | null } | null>(null);

    const { data, loading, error, invoke: reload } = useFetch<TyApiResponse<TyEditorData>>("/menu/editor");

    const {
        data: savedCategories,
        loading: savingCategories,
        error: categoriesError,
        invoke: saveCategories,
    } = useFetch<TyApiResponse<{ categories: TyEditorCategory[] }>>("/menu/categories-bulk-write", { method: "POST" }, false);

    const {
        data: savedProducts,
        loading: savingProducts,
        error: productsError,
        invoke: saveProducts,
    } = useFetch<TyApiResponse<{ products: TyEditorProduct[] }>>("/menu/products-bulk-write", { method: "POST" }, false);

    // --- Sync from the server --------------------------------------------------------------------

    useEffect(() => {
        if (!data?.success) return;
        setCategoryRows(data.data.categories.map((category) => toCategoryRow(category, languages)));
        setProductRows(data.data.products.map((product) => toProductRow(product, languages)));
    }, [data]);

    const dirtyCategories = categoryRows.filter(isRowDirty);
    const dirtyProducts = productRows.filter(isRowDirty);
    const dirtyCount = dirtyCategories.length + dirtyProducts.length;
    const isSaving = savingCategories || savingProducts;

    /**
     * The bulk endpoints reject an invalid batch whole, which would tell the editor "something is
     * wrong" without saying where. Catching it here lets the offending rows be pointed at instead.
     */
    const invalidCategoryKeys = new Set(dirtyCategories.filter((row) => !hasAnyLangValue(row.draft.name)).map((row) => row.key));
    const invalidProductKeys = new Set(
        dirtyProducts.filter((row) => !hasAnyLangValue(row.draft.name) || row.draft.category.length === 0).map((row) => row.key)
    );
    const invalidCount = invalidCategoryKeys.size + invalidProductKeys.size;

    /** Products are saved after categories so a brand-new category can be picked in the same session. */
    const productsQueued = useRef(false);

    const submitProducts = () => {
        const pending = productRows.filter(isRowDirty);
        if (pending.length === 0) return;
        saveProducts({ method: "POST", data: { products: pending.map((row) => productPayload(row, languages, defaultLang)) } });
    };

    useEffect(() => {
        if (!savedCategories?.success) return;
        setCategoryRows(savedCategories.data.categories.map((category) => toCategoryRow(category, languages)));
        if (productsQueued.current) {
            productsQueued.current = false;
            submitProducts();
        }
    }, [savedCategories]);

    useEffect(() => {
        if (!savedProducts?.success) return;
        setProductRows(savedProducts.data.products.map((product) => toProductRow(product, languages)));
    }, [savedProducts]);

    // --- Row editing -----------------------------------------------------------------------------

    const patchCategory = (key: string, patch: Partial<TyCategoryDraft>) =>
        setCategoryRows((rows) => rows.map((row) => (row.key === key ? { ...row, draft: { ...row.draft, ...patch } } : row)));

    const patchProduct = (key: string, patch: Partial<TyProductDraft>) =>
        setProductRows((rows) => rows.map((row) => (row.key === key ? { ...row, draft: { ...row.draft, ...patch } } : row)));

    const revertCategory = (key: string) =>
        setCategoryRows((rows) => rows.map((row) => (row.key === key && row.saved ? { ...row, draft: structuredClone(row.saved) } : row)));

    const revertProduct = (key: string) =>
        setProductRows((rows) => rows.map((row) => (row.key === key && row.saved ? { ...row, draft: structuredClone(row.saved) } : row)));

    const removeCategory = (key: string) => setCategoryRows((rows) => rows.filter((row) => row.key !== key));

    const removeProduct = (key: string) => setProductRows((rows) => rows.filter((row) => row.key !== key));

    /** `column` is set only when Enter grew the grid — the button leaves it alone and starts at the first cell. */
    const addRow = (column: string | null = null) => {
        const key = nextRowKey();
        pendingScrollKey.current = { key, column };

        if (tab === "categories") {
            const nextSort = categoryRows.reduce((max, row) => Math.max(max, row.draft.sort), 0) + 1;
            setCategoryRows((rows) => [...rows, { key, draft: emptyCategoryDraft(nextSort), saved: null }]);
            return;
        }

        // A row added under a filter belongs to what is being filtered for — otherwise it would vanish
        // the moment it is created. Falling back to pre-filling the only category saves a tap per row
        // when a store has just one.
        const onlyCategory = categoryRows.length === 1 && categoryRows[0]._id ? [categoryRows[0]._id] : [];
        const presetCategories = categoryFilter.length > 0 ? [...categoryFilter] : onlyCategory;
        setProductRows((rows) => [...rows, { key, draft: emptyProductDraft(presetCategories), saved: null }]);
    };

    /** A row appended below the fold is a row the user cannot type into — bring it to them. */
    useEffect(() => {
        const pending = pendingScrollKey.current;
        if (!pending) return;
        pendingScrollKey.current = null;

        const row = document.querySelector<HTMLTableRowElement>(`[data-row-key="${pending.key}"]`);
        row?.scrollIntoView({ behavior: "smooth", block: "center" });

        // Enter stays in the column it was pressed in, so a name column can be typed straight down.
        const cell = pending.column ? row?.querySelector<HTMLInputElement>(`[data-cell-column="${pending.column}"]`) : null;
        (cell ?? row?.querySelector<HTMLInputElement>("input"))?.focus();
    }, [categoryRows.length, productRows.length]);

    const discardChanges = () => {
        setCategoryRows((rows) => rows.filter((row) => row._id).map((row) => (row.saved ? { ...row, draft: structuredClone(row.saved) } : row)));
        setProductRows((rows) => rows.filter((row) => row._id).map((row) => (row.saved ? { ...row, draft: structuredClone(row.saved) } : row)));
    };

    // --- Saving ----------------------------------------------------------------------------------

    const handleSave = () => {
        if (dirtyCount === 0 || isSaving || invalidCount > 0) return;

        if (dirtyCategories.length > 0) {
            productsQueued.current = dirtyProducts.length > 0;
            saveCategories({ method: "POST", data: { categories: dirtyCategories.map((row) => categoryPayload(row, languages, defaultLang)) } });
            return;
        }
        submitProducts();
    };

    /** Ctrl/Cmd+S is muscle memory for anyone who spends the day in a spreadsheet. */
    useEffect(() => {
        const handleShortcut = (event: KeyboardEvent) => {
            if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "s") return;
            event.preventDefault();
            handleSave();
        };
        window.addEventListener("keydown", handleShortcut);
        return () => window.removeEventListener("keydown", handleShortcut);
    });

    /** Closing the tab mid-edit would lose the batch, and nothing here autosaves. */
    useEffect(() => {
        if (dirtyCount === 0) return;
        const warn = (event: BeforeUnloadEvent) => event.preventDefault();
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [dirtyCount]);

    /**
     * Re-uses the dashboard's own drag-to-sort popup. It writes the new order straight to the server,
     * so it is only offered on a clean sheet — reloading afterwards would throw away unsaved rows.
     */
    const openReorder = () => {
        const name = "sort-categories";
        removeComp(name);
        addComp({
            name,
            Component: SortCategoriesPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: {
                categories: categoryRows
                    .filter((row) => row._id)
                    .map((row) => ({
                        _id: row._id as string,
                        name: pickLangValue(row.draft.name, lang) || "—",
                        productsCount: row.draft.productsCount,
                        sort: row.draft.sort,
                    })),
                onSuccess: () => reload(),
            },
        });
    };

    // --- Derived ---------------------------------------------------------------------------------

    /** Only saved categories can be assigned — a row with no id yet has nothing to reference. */
    const categoryOptions = useMemo(
        () =>
            categoryRows
                .filter((row) => row._id)
                .map((row) => ({
                    _id: row._id as string,
                    label: pickLangValue(row.draft.name, lang) || "—",
                })),
        [categoryRows, lang]
    );

    /** A product matches when it sits in any of the chosen categories, not all of them. */
    const visibleProductRows = useMemo(
        () => (categoryFilter.length === 0 ? productRows : productRows.filter((row) => row.draft.category.some((id) => categoryFilter.includes(id)))),
        [productRows, categoryFilter]
    );

    // --- Render ----------------------------------------------------------------------------------

    const saveError = categoriesError ?? productsError;
    const dir = lang === "ar" ? "rtl" : "ltr";
    const textFieldSx = getTextFieldSx(dir as "rtl" | "ltr");

    return (
        <div className="menu-editor">
            <ActionsBar>
                <div className="menu-editor-tabs" role="tablist">
                    {(["categories", "products"] as TyTab[]).map((value) => {
                        const isOn = tab === value;
                        const pending = value === "categories" ? dirtyCategories.length : dirtyProducts.length;
                        const total = value === "categories" ? categoryRows.length : productRows.length;
                        return (
                            <button
                                key={value}
                                type="button"
                                role="tab"
                                aria-selected={isOn}
                                className={`menu-editor-tab${isOn ? " menu-editor-tab--on" : ""}`}
                                onClick={() => setTab(value)}
                            >
                                {t.menuEditor[value]}
                                <span className="menu-editor-tab-count">{total}</span>
                                {pending > 0 && <span className="menu-editor-tab-dot" />}
                            </button>
                        );
                    })}
                </div>

                <div className="menu-editor-bar-actions">
                    {tab === "categories" && categoryRows.filter((row) => row._id).length > 1 && (
                        <button
                            type="button"
                            className="menu-editor-icon-btn"
                            onClick={openReorder}
                            disabled={dirtyCount > 0}
                            title={dirtyCount > 0 ? t.menuEditor.saveBeforeReorder : t.menuEditor.reorder}
                            aria-label={t.menuEditor.reorder}
                        >
                            <img src="/icons/dragging-icon.svg" alt="" width={16} height={16} />
                        </button>
                    )}

                    {dirtyCount > 0 && (
                        <button type="button" className="menu-editor-discard" onClick={discardChanges} disabled={isSaving}>
                            {t.menuEditor.discard}
                        </button>
                    )}

                    {dirtyCount > 0 && (
                        <button type="button" className="menu-editor-save" onClick={handleSave} disabled={isSaving || invalidCount > 0}>
                            {isSaving ? <span className="menu-editor-spinner" /> : `${t.menuEditor.save} (${dirtyCount})`}
                        </button>
                    )}
                </div>
            </ActionsBar>

            <div className="menu-editor-content">
                {invalidCount > 0 && <p className="menu-editor-alert menu-editor-alert--warn">{t.menuEditor.fixRowsFirst}</p>}
                {saveError && <p className="menu-editor-alert menu-editor-alert--error">{saveError}</p>}

                {loading && <p className="menu-editor-note">{t.menuEditor.loading}</p>}

                {!loading && error && (
                    <div className="menu-editor-note">
                        <p className="menu-editor-alert menu-editor-alert--error">{error}</p>
                        <button type="button" className="menu-editor-discard" onClick={() => reload()}>
                            {t.menuEditor.retry}
                        </button>
                    </div>
                )}

                {!loading && !error && tab === "products" && categoryOptions.length > 0 && (
                    <div className="menu-editor-filter">
                        <FormControl fullWidth size="small" sx={textFieldSx}>
                            <InputLabel>{t.menuEditor.filterByCategories}</InputLabel>
                            <Select
                                multiple
                                value={categoryFilter}
                                onChange={(event) => setCategoryFilter(event.target.value as string[])}
                                input={<OutlinedInput label={t.menuEditor.filterByCategories} />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {(selected as string[]).map((id) => (
                                            <Chip
                                                key={id}
                                                label={categoryOptions.find((option) => option._id === id)?.label ?? "—"}
                                                size="small"
                                                sx={{
                                                    backgroundColor: "var(--color-shaded-brand)",
                                                    color: "var(--color-dark)",
                                                    border: "1px solid var(--color-mid-brand)",
                                                    height: "24px",
                                                    fontFamily: "Alexandria, sans-serif",
                                                    "& .MuiChip-label": { fontSize: "12px", fontWeight: 600, padding: "0 8px" },
                                                }}
                                            />
                                        ))}
                                    </Box>
                                )}
                                MenuProps={{ slotProps: { paper: { style: { maxHeight: 48 * 4.5 + 8 } } } }}
                            >
                                {categoryOptions.map((option) => (
                                    <MenuItem key={option._id} value={option._id}>
                                        <Checkbox
                                            checked={categoryFilter.includes(option._id)}
                                            sx={{ color: "var(--color-gray-01)", "&.Mui-checked": { color: "var(--color-brand)" } }}
                                        />
                                        <ListItemText
                                            primary={option.label}
                                            sx={{ "& .MuiTypography-root": { fontFamily: "Alexandria, sans-serif", fontSize: "13.5px" } }}
                                        />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {categoryFilter.length > 0 && (
                            <button
                                type="button"
                                className="menu-editor-icon-btn"
                                onClick={() => setCategoryFilter([])}
                                title={t.menuEditor.clearFilter}
                                aria-label={t.menuEditor.clearFilter}
                            >
                                <img src="/icons/clear-icon.svg" alt="" width={18} height={18} />
                            </button>
                        )}
                    </div>
                )}

                {!loading && !error && (
                    <div className="menu-editor-sheet">
                        {/* Only this wrapper scrolls sideways; the page itself carries the vertical scroll. */}
                        <div className="menu-editor-scroll-x">
                            {tab === "categories" ? (
                                <CategoriesGrid
                                    onAddRow={addRow}
                                    rows={categoryRows}
                                    invalidKeys={invalidCategoryKeys}
                                    onChange={patchCategory}
                                    onRevert={revertCategory}
                                    onRemove={removeCategory}
                                />
                            ) : (
                                <ProductsGrid
                                    onAddRow={addRow}
                                    rows={visibleProductRows}
                                    invalidKeys={invalidProductKeys}
                                    categoryOptions={categoryOptions}
                                    onChange={patchProduct}
                                    onRevert={revertProduct}
                                    onRemove={removeProduct}
                                />
                            )}
                        </div>

                        {tab === "products" && categoryFilter.length > 0 && visibleProductRows.length === 0 && (
                            <p className="menu-editor-alert">{t.menuEditor.noRowsForFilter}</p>
                        )}

                        {/* Sticks to the foot of the viewport so a new row is always one tap away. */}
                        <button type="button" className="menu-editor-add-row" onClick={() => addRow()}>
                            + {t.menuEditor.addRow}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
